import React, { ReactElement } from "react";
import { ProcessedMetadata } from "../client/Metadata";
import { ifFileOrUrl, ifString } from "../client";
import { DoubleRowDisplay, FileDisplay, Image, KeyDisplay } from "./display";
import { FileData } from "./inputs/fileRow";

interface Props {
  collection?: String;
  tokenId?: BigInt;
  metadata: ProcessedMetadata;
  decryptionKey?: String;
  owner?: String;
}

export function CnftDetailTable({
  collection,
  tokenId,
  metadata,
  decryptionKey,
  owner,
}: Props) {
  // Some values only exist if metadata does contain the encrypted data.
  const isPublishedCNFT = metadata.confidential !== undefined;

  // List of fields with special handling, they are not displayed as "Unknown Field"
  const known_fields = [
    "image",
    "confidential",
    "name",
    "description",
    "files",
  ];
  const unknown_fields = [
    ...new Set(
      Object.keys(metadata)
        .map((key) => key.replace(/\$confidential$/, ""))
        .filter((key) => !known_fields.includes(key)),
    ),
  ].sort();

  // NOTE: This currently does not contain special marking for confidential.
  const rows_extra_field = unknown_fields.map((field) => (
    <DoubleRowDisplay
      key={`extra-${field}`}
      label={`Extra field: ${field}`}
      pub={metadata[field]}
      confidential={metadata[field + "$confidential"]}
      render={unknownField}
    />
  ));

  const show_technical_details =
    collection || tokenId || owner || isPublishedCNFT;

  let files;
  if (Array.isArray(metadata.files)) {
    // TODO: Figure out how to handle the case when files contains bad
    // information properly.
    files = metadata.files.map((file, i) => (
      <FileDisplay key={i} file={file as FileData} />
    ));
  }

  // NOTE: If the file type does not match the expected value it is not shown
  // (intentionally).
  //
  // TODO: In the long run we probably want to split this into a display for the
  // image, an area for file uploads and one or multiple tables for the
  // text-like fields. Instead of having one big table.
  return (
    <table className="nft-details">
      <thead>
        <tr key="row-header1">
          <th className="field-column">Field</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <DoubleRowDisplay
          key="image"
          label="Image"
          pub={ifFileOrUrl(metadata.image)}
          confidential={ifFileOrUrl(metadata.image$confidential)}
          render={(value) => <Image className="nft-image" src={value} />}
        />
        <DoubleRowDisplay
          key="name"
          label="Name"
          pub={ifString(metadata.name)}
          confidential={ifString(metadata.name$confidential)}
        />
        <DoubleRowDisplay
          key="description"
          label="Description"
          pub={ifString(metadata.description)}
          confidential={ifString(metadata.description$confidential)}
        />
        {rows_extra_field}
        <tr>
          <td>Files</td>
          <td className="file-display-container">{files}</td>
        </tr>

        {show_technical_details && (
          <tr key="header2">
            <th className="field-column">Technical Details</th>
            <th colSpan={2}>Value (always public)</th>
          </tr>
        )}
        {collection && (
          <tr key="collection">
            <td>Contract address</td>
            <td>
              <a href={`https://sepolia.etherscan.io/address/${collection}`}>
                {collection}
              </a>
            </td>
          </tr>
        )}
        {tokenId && (
          <tr key="tokenid">
            <td>Token ID</td>
            <td>
              {tokenId?.toString()} (
              <a
                href={`https://testnets.opensea.io/assets/sepolia/${collection}/${tokenId}`}
              >
                OpenSea
              </a>
              )
            </td>
          </tr>
        )}
        {owner && (
          <tr key="owner">
            <td>Current Owner</td>
            <td>
              <a href={`https://sepolia.etherscan.io/address/${owner}`}>
                {owner}
              </a>
            </td>
          </tr>
        )}

        {/* {isPublishedCNFT &&
          fixed_row(
            "row-cdata-size",
            "Confidential data size",
            confidential_data_size
          )} */}
        {isPublishedCNFT &&
          fixed_row(
            "row-key-id",
            "Encryption Key-ID",
            metadata.confidential?.key_id,
          )}
        {decryptionKey &&
          fixed_row(
            "row-key",
            "Decryption Key",
            <KeyDisplay decryptionKey={decryptionKey} />,
          )}
      </tbody>
    </table>
  );
}

function unknownField(value: any): string {
  if (typeof value === "object") {
    // Object or array
    return JSON.stringify(value);
  } else {
    return value;
  }
}

function fixed_row(
  key: string,
  name: string | ReactElement,
  value: string | ReactElement | undefined | null,
): ReactElement {
  return (
    <tr key={key}>
      <td>{name}</td>
      <td>{value}</td>
    </tr>
  );
}
